import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Shield, 
  Settings, 
  Users, 
  CheckCircle2, 
  ArrowRight, 
  Zap, 
  Cpu, 
  Activity, 
  Phone, 
  Mail, 
  MapPin, 
  Building, 
  Send, 
  AlertCircle,
  Clock,
  Wrench,
  FileText
} from 'lucide-react';
import { LOGOS, FALLBACK_REFERENCES } from '../data';

const HERO_IMAGES = [
  "https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/5c958af8-7b31-4598-b97e-62220a72b716.jpeg",
  "https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/237ca15e-31ae-496d-a72d-cf3337102408.jpeg",
  "https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/afea74fa-cf1a-45a7-81dc-6c431b572c30.jpeg"
];

export default function Home() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [logosList, setLogosList] = useState(LOGOS);
  const [pageData, setPageData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/by-slug/da/index')
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Failed to fetch home page data');
      })
      .then(data => {
        setPageData(data);
      })
      .catch(err => {
        console.error('Error fetching home page data:', err);
      });
  }, []);

  const heroImages = pageData?.hero_images && pageData.hero_images.length > 0 
    ? pageData.hero_images 
    : HERO_IMAGES;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [heroImages]);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/logos')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch logos');
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const sorted = [...data].sort((a, b) => {
            const orderA = a.order !== undefined && a.order !== null ? Number(a.order) : 999;
            const orderB = b.order !== undefined && b.order !== null ? Number(b.order) : 999;
            return orderA - orderB;
          });
          
          const mapped = sorted.map(item => ({
            name: item.name,
            url: Array.isArray(item.logo) && item.logo.length > 0 ? item.logo[0] : ''
          })).filter(item => item.url);
          
          if (mapped.length > 0) {
            setLogosList(mapped);
          }
        }
      })
      .catch(err => {
        console.error('Error fetching logos from CMS:', err);
      });
  }, []);

  const getOptimizedUrl = (url, width = 300) => {
    if (!url || !url.startsWith('https://assets.auxodev.dk/')) return url;
    if (url.includes('/cdn-cgi/image/')) return url;
    return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setStatus({ type: 'error', message: 'Udfyld venligst alle påkrævede felter.' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Mange tak for din henvendelse! Vi vender tilbage til dig hurtigst muligt for at aftale en samtale.'
        });
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        });
      } else {
        throw new Error('Serverfejl');
      }
    } catch (err) {
      setStatus({
        type: 'error',
        message: 'Der opstod en fejl under afsendelsen. Prøv venligst igen eller kontakt os direkte på llk@carpa.dk.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = pageData?.title?.da || pageData?.title || "Fra projekt til sikker drift - CARPA ApS";
  const desc = pageData?.meta_description?.da || pageData?.meta_description || "CARPA hjælper PtX-, energi- og procesindustrien med commissioning, operational readiness, technical leadership og handover. Vi skaber struktur i den kritiske fase.";

  return (
    <div className="bg-[#f7f8fa] text-[#0e1f2f] min-h-screen font-sans antialiased">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={desc} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc} />
        <meta property="og:image" content={heroImages[0]} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex flex-col justify-center p-6 md:p-12 lg:p-24 overflow-hidden bg-[#0e1f2f] text-white">
        {/* Background Image Slideshow Overlay */}
        <div className="absolute inset-0 z-0">
          {heroImages.map((imgUrl, idx) => (
            <img
              key={imgUrl}
              src={imgUrl}
              alt="PtX-anlæg og industri"
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1500 ease-in-out mix-blend-multiply ${
                idx === currentImageIndex ? 'opacity-50' : 'opacity-0'
              }`}
            />
          ))}
          {/* Subtle Technical Grid Overlay */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:40px_40px]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0e1f2f] via-[#0e1f2f]/90 to-transparent" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-4xl space-y-8 my-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-[#3c8888]/20 backdrop-blur-md border border-[#3c8888]/30 px-4 py-1.5 rounded-full">
            <span className="h-2 w-2 rounded-full bg-[#3c8888] animate-pulse" />
            <span className="text-xs font-mono font-bold uppercase tracking-widest text-white">
              {pageData?.hero_badge?.da || pageData?.hero_badge || "Sikker opstart & stabil drift"}
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-tight text-white">
            {pageData?.hero_title?.da || pageData?.hero_title || "Fra projekt til sikker drift"}
          </h1>

          <p className="text-lg sm:text-xl text-stone-200 font-medium leading-relaxed max-w-3xl">
            {pageData?.hero_description?.da || pageData?.hero_description || "CARPA hjælper PtX-, energi- og procesindustrien med commissioning, operational readiness, technical leadership og handover. Vi skaber struktur i den kritiske fase, hvor komplekse anlæg skal testes, overleveres og bringes sikkert i drift."}
          </p>

          <div className="flex flex-col sm:flex-row items-center gap-4 pt-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-md bg-[#3c8888] px-8 py-4 text-base font-bold text-white shadow-lg hover:bg-[#3c8888]/90 transition-all duration-200 border-b-2 border-[#3c8888]"
            >
              Book en afklarende samtale
            </Link>
            <a
              href="tel:+4553636223"
              className="w-full sm:w-auto inline-flex items-center justify-center rounded-md border border-white/20 bg-white/10 backdrop-blur-sm px-8 py-4 text-base font-bold text-white shadow-sm hover:bg-white/20 transition-all duration-200"
            >
              Kontakt Laust
            </a>
          </div>
        </div>
      </section>

      {/* Intro Section - Short, Visual, Easy to scan */}
      <section className="py-20 md:py-28 bg-white border-b border-[#0e1f2f]/10">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="flex items-center gap-4">
                <div className="h-[3px] w-12 bg-[#3c8888]" />
                <span className="text-xs font-mono font-bold uppercase tracking-widest text-[#3c8888]">
                  {pageData?.intro_badge?.da || pageData?.intro_badge || "Kernefortælling"}
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0e1f2f] tracking-tight leading-tight">
                {pageData?.intro_title?.da || pageData?.intro_title || "Teknisk tyngde. Praktisk eksekvering. Sikker opstart."}
              </h2>
              <p className="text-lg text-stone-600 leading-relaxed">
                {pageData?.intro_description?.da || pageData?.intro_description || "CARPA arbejder i krydsfeltet mellem projekt, leverandører og drift. Vi sikrer, at anlæg, organisation og processer er klar, før overleveringen sker."}
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="relative aspect-video lg:aspect-square rounded-xl overflow-hidden border border-[#0e1f2f]/10 shadow-md">
                <img
                  src={pageData?.intro_image?.[0] || "https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/afe43705-5832-4975-9507-5d77c6c4492e.jpg"}
                  alt="Site walkdown"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Positionering Section - Hands-on partner */}
      <section className="py-20 md:py-28 bg-[#f7f8fa] border-b border-[#0e1f2f]/10">
        <div className="mx-auto max-w-7xl px-6 space-y-16">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <span className="text-xs font-mono font-bold uppercase tracking-widest text-[#3c8888]">
              Vores Positionering
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0e1f2f] tracking-tight">
              En hands-on teknisk partner tæt på anlægget
            </h2>
            <p className="text-stone-600 text-base sm:text-lg">
              CARPA er ikke et klassisk konsulenthus — CARPA er en hands-on teknisk partner tæt på anlægget. Vi omsætter komplekse planer til praktisk handling.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1: Teknisk */}
            <div className="bg-white rounded-xl border border-[#0e1f2f]/10 p-8 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-4">
              <div className="h-12 w-12 rounded-lg bg-[#3c8888]/10 text-[#3c8888] flex items-center justify-center">
                <Wrench className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-[#0e1f2f]">Teknisk</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Hands-on erfaring fra anlæg, systemer, test, opstart og drift. Vi forstår maskineriet og de tekniske detaljer.
              </p>
            </div>

            {/* Card 2: Struktureret */}
            <div className="bg-white rounded-xl border border-[#0e1f2f]/10 p-8 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-4">
              <div className="h-12 w-12 rounded-lg bg-[#0e1f2f]/10 text-[#0e1f2f] flex items-center justify-center">
                <FileText className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-[#0e1f2f]">Struktureret</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Planer, interfaces, punch, test, dokumentation og overdragelse. Vi skaber struktur i det kritiske krydsfelt.
              </p>
            </div>

            {/* Card 3: Driftsklar */}
            <div className="bg-white rounded-xl border border-[#0e1f2f]/10 p-8 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-4">
              <div className="h-12 w-12 rounded-lg bg-[#3c8888]/10 text-[#3c8888] flex items-center justify-center">
                <Activity className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-[#0e1f2f]">Driftsklar</h3>
              <p className="text-stone-600 text-sm leading-relaxed">
                Fra mekanisk færdigt anlæg til stabil drift og ejerskab i organisationen. Vi sikrer, at driftsteamet er klar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ydelser Section - Modern, Visual Cards */}
      <section className="py-20 md:py-28 bg-white border-b border-[#0e1f2f]/10">
        <div className="mx-auto max-w-7xl px-6 space-y-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="space-y-4 max-w-2xl">
              <span className="text-xs font-mono font-bold uppercase tracking-widest text-[#3c8888]">
                Hvad vi tilbyder
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0e1f2f] tracking-tight">
                Vores primære ydelser
              </h2>
            </div>
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-2 text-sm font-bold text-[#3c8888] hover:text-[#3c8888]/80 transition-colors"
            >
              <span>Se alle ydelser i detaljer</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Ydelse 1: Commissioning Management */}
            <div className="bg-[#f7f8fa] rounded-xl border border-[#0e1f2f]/10 p-8 flex flex-col justify-between gap-6 hover:border-[#3c8888]/30 transition-colors">
              <div className="space-y-4">
                <div className="h-10 w-10 rounded-lg bg-[#3c8888]/10 text-[#3c8888] flex items-center justify-center">
                  <Settings className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-[#0e1f2f]">Commissioning Management</h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Planlægning og styring af test, interfaces, punch, dokumentation og systemoverdragelse. Fra mekanisk færdigt anlæg til verificeret funktion.
                </p>
              </div>
            </div>

            {/* Ydelse 2: Operational Readiness */}
            <div className="bg-[#f7f8fa] rounded-xl border border-[#0e1f2f]/10 p-8 flex flex-col justify-between gap-6 hover:border-[#3c8888]/30 transition-colors">
              <div className="space-y-4">
                <div className="h-10 w-10 rounded-lg bg-[#0e1f2f]/10 text-[#0e1f2f] flex items-center justify-center">
                  <Activity className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-[#0e1f2f]">Operational Readiness</h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Vi gør driftsteam, procedurer, reservedele, dokumentation, CMMS-grundlag og processer klar til overtagelse.
                </p>
              </div>
            </div>

            {/* Ydelse 3: Technical Leadership */}
            <div className="bg-[#f7f8fa] rounded-xl border border-[#0e1f2f]/10 p-8 flex flex-col justify-between gap-6 hover:border-[#3c8888]/30 transition-colors">
              <div className="space-y-4">
                <div className="h-10 w-10 rounded-lg bg-[#3c8888]/10 text-[#3c8888] flex items-center justify-center">
                  <Users className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-[#0e1f2f]">Technical Leadership</h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Teknisk ledelse tæt på anlægget — med fokus på fremdrift, beslutninger, leverandørstyring og kvalitet.
                </p>
              </div>
            </div>

            {/* Ydelse 4: Handover & Start-up Support */}
            <div className="bg-[#f7f8fa] rounded-xl border border-[#0e1f2f]/10 p-8 flex flex-col justify-between gap-6 hover:border-[#3c8888]/30 transition-colors">
              <div className="space-y-4">
                <div className="h-10 w-10 rounded-lg bg-[#0e1f2f]/10 text-[#0e1f2f] flex items-center justify-center">
                  <CheckCircle2 className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-[#0e1f2f]">Handover & Start-up Support</h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Støtte i overgangen fra projekt til drift, hvor viden, ansvar og ejerskab skal flyttes uden tab af kontrol.
                </p>
              </div>
            </div>

            {/* Ydelse 5: Strategic Maintenance */}
            <div className="bg-[#f7f8fa] rounded-xl border border-[#0e1f2f]/10 p-8 flex flex-col justify-between gap-6 hover:border-[#3c8888]/30 transition-colors">
              <div className="space-y-4">
                <div className="h-10 w-10 rounded-lg bg-[#3c8888]/10 text-[#3c8888] flex items-center justify-center">
                  <Wrench className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-[#0e1f2f]">Strategic Maintenance</h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Opbygning af vedligeholdsstruktur, reservedelsstrategi, CMMS-grundlag og praktiske løsninger, der reducerer nedetid.
                </p>
              </div>
            </div>

            {/* Ydelse 6: Risk & Safety (Generisk specialistområde) */}
            <div className="bg-[#f7f8fa] rounded-xl border border-[#0e1f2f]/10 p-8 flex flex-col justify-between gap-6 hover:border-[#3c8888]/30 transition-colors">
              <div className="space-y-4">
                <div className="h-10 w-10 rounded-lg bg-stone-200 text-stone-600 flex items-center justify-center">
                  <Shield className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold text-[#0e1f2f]">Risk & Safety</h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Generisk specialistområde og netværksfokus. Vi rådgiver om funktionel sikkerhed og risikostyring på komplekse anlæg.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Metode-sektion - Simple visual process */}
      <section className="py-20 md:py-28 bg-[#f7f8fa] border-b border-[#0e1f2f]/10">
        <div className="mx-auto max-w-7xl px-6 space-y-16">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <span className="text-xs font-mono font-bold uppercase tracking-widest text-[#3c8888]">
              Vores Metode
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0e1f2f] tracking-tight">
              Enkel og struktureret proces
            </h2>
            <p className="text-stone-600 text-sm sm:text-base">
              CARPA skaber struktur fra projektfase til sikker drift gennem planlægning, test, dokumentation, overdragelse og praktisk opfølgning.
            </p>
          </div>

          {/* Process Steps */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
            {/* Step 1 */}
            <div className="bg-white rounded-xl border border-[#0e1f2f]/10 p-6 shadow-sm text-center relative space-y-3">
              <div className="mx-auto h-10 w-10 rounded-full bg-[#3c8888] text-white flex items-center justify-center font-mono font-bold text-sm">
                01
              </div>
              <h4 className="font-bold text-lg text-[#0e1f2f]">Plan</h4>
              <p className="text-stone-500 text-xs leading-relaxed">
                Tidlig planlægning, interface-styring og opbygning af testgrundlag.
              </p>
            </div>

            {/* Step 2 */}
            <div className="bg-white rounded-xl border border-[#0e1f2f]/10 p-6 shadow-sm text-center relative space-y-3">
              <div className="mx-auto h-10 w-10 rounded-full bg-[#0e1f2f] text-white flex items-center justify-center font-mono font-bold text-sm">
                02
              </div>
              <h4 className="font-bold text-lg text-[#0e1f2f]">Test</h4>
              <p className="text-stone-500 text-xs leading-relaxed">
                Systematisk test, FAT/SAT, og verificering af alle funktioner.
              </p>
            </div>

            {/* Step 3 */}
            <div className="bg-white rounded-xl border border-[#0e1f2f]/10 p-6 shadow-sm text-center relative space-y-3">
              <div className="mx-auto h-10 w-10 rounded-full bg-[#3c8888] text-white flex items-center justify-center font-mono font-bold text-sm">
                03
              </div>
              <h4 className="font-bold text-lg text-[#0e1f2f]">Handover</h4>
              <p className="text-stone-500 text-xs leading-relaxed">
                Struktureret overdragelse af viden, ansvar og dokumentation.
              </p>
            </div>

            {/* Step 4 */}
            <div className="bg-white rounded-xl border border-[#0e1f2f]/10 p-6 shadow-sm text-center relative space-y-3">
              <div className="mx-auto h-10 w-10 rounded-full bg-[#0e1f2f] text-white flex items-center justify-center font-mono font-bold text-sm">
                04
              </div>
              <h4 className="font-bold text-lg text-[#0e1f2f]">Drift</h4>
              <p className="text-stone-500 text-xs leading-relaxed">
                Stabil og sikker drift med fuldt ejerskab i driftsorganisationen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Referencer Section - Trustworthy project showcase */}
      <section className="py-20 md:py-28 bg-white border-b border-[#0e1f2f]/10">
        <div className="mx-auto max-w-7xl px-6 space-y-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="space-y-4 max-w-2xl">
              <span className="text-xs font-mono font-bold uppercase tracking-widest text-[#3c8888]">
                Dokumenteret Erfaring
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0e1f2f] tracking-tight">
                Referencer
              </h2>
              <p className="text-stone-600 text-sm sm:text-base">
                Vi viser vores erfaring og troværdighed gennem konkrete projekter uden at gå på kompromis med fortrolighed.
              </p>
            </div>
            <Link
              to="/referencer"
              className="inline-flex items-center gap-2 text-sm font-bold text-[#3c8888] hover:text-[#3c8888]/80 transition-colors"
            >
              <span>Se alle referencer</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {FALLBACK_REFERENCES.map((project) => (
              <div 
                key={project.id}
                className="bg-white border border-[#0e1f2f]/10 rounded-xl overflow-hidden shadow-sm flex flex-col justify-between hover:border-[#3c8888]/30 transition-colors"
              >
                <div>
                  <div className="relative aspect-[16/10] overflow-hidden bg-stone-100">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="p-6 space-y-3">
                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-[#3c8888] block">
                      {project.project_type}
                    </span>
                    <h3 className="text-lg font-bold text-[#0e1f2f] leading-snug">
                      {project.title}
                    </h3>
                    <p className="text-xs text-stone-500 font-medium">
                      Klient: {project.client}
                    </p>
                    <p className="text-stone-600 text-xs leading-relaxed line-clamp-3">
                      {project.description}
                    </p>
                  </div>
                </div>
                <div className="p-6 pt-0">
                  <Link
                    to="/referencer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-[#3c8888] hover:text-[#3c8888]/80 transition-colors"
                  >
                    <span>Læs mere</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Om CARPA Teaser */}
      <section className="py-20 md:py-28 bg-[#f7f8fa] border-b border-[#0e1f2f]/10">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="relative aspect-video lg:aspect-square rounded-xl overflow-hidden border border-[#0e1f2f]/10 shadow-md">
                <img
                  src={pageData?.intro_image?.[1] || "https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/08508acd-3c99-43a6-ad1d-6683a0f16c26.jpg"}
                  alt="Commissioning"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-mono font-bold uppercase tracking-widest text-[#3c8888]">
                Hvem er vi
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0e1f2f] tracking-tight">
                Fra komplekse tekniske anlæg til stabil drift
              </h2>
              {pageData?.additional_content?.da ? (
                <div 
                  className="text-stone-600 text-sm sm:text-base leading-relaxed space-y-4 prose max-w-none prose-stone [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-[#0e1f2f] [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-[#0e1f2f] [&_h3]:mt-4 [&_h3]:mb-2 [&_p]:mb-4 [&_strong]:text-[#0e1f2f]"
                  dangerouslySetInnerHTML={{ __html: pageData.additional_content.da }}
                />
              ) : (
                <>
                  <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                    CARPA hjælper industri-, energi- og procesvirksomheder med at skabe struktur, fremdrift og teknisk kontrol i overgangen fra projekt til drift.
                  </p>
                  <p className="text-stone-600 text-sm sm:text-base leading-relaxed">
                    Vi kombinerer praktisk site-erfaring, commissioning-ledelse og teknisk forståelse med en hands-on tilgang til test, dokumentation, handover, drift og vedligehold.
                  </p>
                </>
              )}
              <div className="pt-4">
                <Link
                  to="/om-carpa"
                  className="inline-flex items-center justify-center rounded-md bg-[#0e1f2f] px-6 py-3 text-sm font-bold text-white shadow-sm hover:bg-[#0e1f2f]/90 transition-colors border-b-2 border-[#3c8888]"
                >
                  Læs mere om os
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logos / Trust Section */}
      <section className="border-b border-[#0e1f2f]/10 bg-white py-12 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6">
          
          <style dangerouslySetInnerHTML={{__html: `
            @keyframes marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .marquee-container {
              display: flex;
              width: max-content;
              animation: marquee 35s linear infinite;
            }
          `}} />

          <div className="relative w-full overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
            
            <div className="marquee-container flex gap-10 items-center">
              {logosList.map((logo, idx) => (
                <div key={`logo-1-${idx}`} className={`flex items-center justify-center rounded-md px-6 py-3 ring-1 ring-black/5 shrink-0 ${(logo.name === 'WMO' || logo.name === 'Ineos') ? 'bg-[#0e1f2f]' : 'bg-white/80'}`}>
                  <img
                    src={getOptimizedUrl(logo.url, 300)}
                    alt={logo.name}
                    className={`h-10 md:h-12 max-w-[150px] object-contain ${(logo.name === 'WMO' || logo.name === 'Ineos') ? 'drop-shadow-[0_2px_8px_rgba(255,255,255,0.15)]' : ''}`}
                  />
                </div>
              ))}
              {logosList.map((logo, idx) => (
                <div key={`logo-2-${idx}`} className={`flex items-center justify-center rounded-md px-6 py-3 ring-1 ring-black/5 shrink-0 ${(logo.name === 'WMO' || logo.name === 'Ineos') ? 'bg-[#0e1f2f]' : 'bg-white/80'}`}>
                  <img
                    src={getOptimizedUrl(logo.url, 300)}
                    alt={logo.name}
                    className={`h-10 md:h-12 max-w-[150px] object-contain ${(logo.name === 'WMO' || logo.name === 'Ineos') ? 'drop-shadow-[0_2px_8px_rgba(255,255,255,0.15)]' : ''}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Book en afklarende samtale / Kontakt Section */}
      <section id="book-samtale" className="py-20 md:py-28 bg-[#0e1f2f] text-white relative overflow-hidden">
        {/* Background Image Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/594540e3-6717-499c-be64-92e37498def2.jpg"
            alt="Kontrolrum"
            className="h-full w-full object-cover opacity-10"
          />
          <div className="absolute inset-0 bg-[#0e1f2f]/95" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left: Contact Info */}
            <div className="lg:col-span-5 space-y-8">
              <div className="space-y-4">
                <span className="text-xs font-mono font-bold uppercase tracking-widest text-[#3c8888]">
                  Tag det næste skridt
                </span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                  Book en afklarende samtale
                </h2>
                <p className="text-stone-300 text-sm sm:text-base leading-relaxed">
                  Lad os tage en uforpligtende snak om jeres anlæg, tidsplaner og udfordringer. Vi hjælper jer med at skabe struktur og sikkerhed fra projekt til stabil drift.
                </p>
              </div>

              <div className="space-y-4 text-sm font-mono text-stone-300">
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-[#3c8888]" />
                  <a href="tel:+4553636223" className="hover:text-white transition-colors">+45 53 63 62 23</a>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-[#3c8888]" />
                  <a href="mailto:llk@carpa.dk" className="hover:text-white transition-colors">llk@carpa.dk</a>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#3c8888] shrink-0 mt-0.5" />
                  <span>Grævlinghøjen 12, 8800 Viborg</span>
                </div>
              </div>

              <div className="pt-6 border-t border-white/10 space-y-2">
                <h4 className="text-xs font-mono uppercase tracking-wider text-stone-400">Din kontaktperson</h4>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full overflow-hidden bg-white/10 border border-white/20">
                    <img 
                      src="https://assets.auxodev.dk/assets/59acd1b6-4973-4db5-aba9-2022467ddb50/ad3a5af6-09f1-4924-9433-0a99ce0d0394.webp" 
                      alt="Laust Kildevæld" 
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div>
                    <p className="font-bold text-sm text-white">Laust Kildevæld</p>
                    <p className="text-xs text-stone-400">Stifter & Senior Consultant</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-7 bg-white/5 backdrop-blur-md border border-white/10 rounded-xl p-6 md:p-8 shadow-xl">
              <h3 className="text-xl font-bold text-white mb-6">Udfyld formularen</h3>

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Honeypot field (Antispam) */}
                <div className="sr-only absolute overflow-hidden h-0 w-0" aria-hidden="true">
                  <label htmlFor="website_url">Do not fill this out if you are human</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleInputChange}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Navn */}
                  <div className="space-y-1.5">
                    <label htmlFor="name" className="text-xs font-mono uppercase tracking-wider text-stone-400 font-bold">
                      Navn *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full rounded-md border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-stone-500 focus:border-[#3c8888] focus:bg-white/10 focus:outline-none focus:ring-1 focus:ring-[#3c8888] transition-colors"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  {/* Telefonnummer */}
                  <div className="space-y-1.5">
                    <label htmlFor="phone" className="text-xs font-mono uppercase tracking-wider text-stone-400 font-bold">
                      Telefonnummer *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full rounded-md border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-stone-500 focus:border-[#3c8888] focus:bg-white/10 focus:outline-none focus:ring-1 focus:ring-[#3c8888] transition-colors"
                      placeholder="+45 00 00 00 00"
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-stone-400 font-bold">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-stone-500 focus:border-[#3c8888] focus:bg-white/10 focus:outline-none focus:ring-1 focus:ring-[#3c8888] transition-colors"
                    placeholder="din.email@virksomhed.dk"
                  />
                </div>

                {/* Beskrivelse */}
                <div className="space-y-1.5">
                  <label htmlFor="description" className="text-xs font-mono uppercase tracking-wider text-stone-400 font-bold">
                    Beskrivelse af jeres behov *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    required
                    rows="4"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full rounded-md border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-stone-500 focus:border-[#3c8888] focus:bg-white/10 focus:outline-none focus:ring-1 focus:ring-[#3c8888] transition-colors resize-none"
                    placeholder="Beskriv jeres anlæg, tidsplan eller spørgsmål..."
                  />
                </div>

                {/* Status Message */}
                {status.message && (
                  <div className={`rounded-md p-4 flex items-start gap-3 text-sm ${
                    status.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-200' : 'bg-red-500/10 border border-red-500/20 text-red-200'
                  }`}>
                    {status.type === 'success' ? <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-400" /> : <AlertCircle className="h-5 w-5 shrink-0 text-red-400" />}
                    <span>{status.message}</span>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-[#3c8888] py-3.5 text-sm font-bold text-white shadow-md hover:bg-[#3c8888]/90 transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed border-b-2 border-[#3c8888]"
                >
                  {isSubmitting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      <span>Sender...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>Send anmodning</span>
                    </>
                  )}
                </button>
              </form>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
